// automation-agent.js — shared propose-only automation form assistant.
//
// Both the web New/v2 dialog and the native tablet editor use this module. The
// model may only propose a fully validated create_automation action; it never
// writes. The caller pre-fills its ordinary form and the clinician still presses
// Create, which runs the same caller-authorised createJobViaMedplum write as a
// manually completed form.
import { askGlobalAgent } from './global-agent.js';
import {
  JOB_ACTIONS,
  TASK_TYPES,
  cohortSummary,
  createJobViaMedplum,
  scheduleSummary,
  taskSummary,
} from './medplum-automations.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLACEHOLDER_TITLE_RE = /^(general( task)?|task|note|to-?do|reminder|todo|item)$/i;
const CHANNELS = ['whatsapp', 'sms', 'inapp'];
const clean = (value) => (value === null || value === undefined ? '' : String(value).trim());
const lower = (value) => clean(value).toLowerCase();

export function toTimeValue(hour, minute) {
  const h = Number(hour);
  const m = Number(minute || 0);
  if (!Number.isFinite(h) || h < 0 || h > 23) return '';
  if (!Number.isFinite(m) || m < 0 || m > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function fromTimeValue(value) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(clean(value));
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

export const createAutomationProposalSpec = {
  roles: ['doctor', 'owner', 'admin'],
  name: 'create_automation',
  describe:
    'Schedule a recurring automation. It requires (1) schedule { freq: "daily"|"weekly", hour 0-23, minute 0-59, dayOfWeek 0-6 for weekly }; '
    + '(2) cohort { patients?: ["Patient/<uuid>"], staff?: ["Practitioner/<uuid>"], query?: a FHIR Patient search }; '
    + '(3) action "send-message" with message { text, channel: "whatsapp"|"sms"|"inapp" }, or action "create-task" with task { title, taskType, ownerId? }. '
    + 'DO NOT STAGE EARLY: wait until who, when, and the exact message or task wording are known; otherwise ask for the ONE missing piece. '
    + 'Every named recipient must be resolved with fhir_request and use the returned UUID; never invent or guess an id. Patient message text is verbatim. '
    + 'CONTRACT ON YOUR REPLY TEXT: if who, when, and the exact wording are ALL known, you MUST call this tool — never just describe the automation in prose instead of staging it. '
    + 'If anything is still missing, your reply is ONLY a question or a request naming that missing piece — it must NEVER use past-tense completed-action language, with or without the subject pronoun '
    + '("I proposed…", "I\'ve created…", "I have scheduled…", "I staged…", "I set that up…", "Proposed a…", "Created a…", "Scheduled a…", "Staged a…") when you did not actually call this tool, because nothing was staged and the form stays empty.',
  params: 'action?: send-message|create-task, schedule: { freq: daily|weekly, hour, minute, dayOfWeek? }, cohort: { patients?: ["Patient/<uuid>"], staff?: ["Practitioner/<uuid>"], query?: "Patient?..." }, message?: { text, channel: whatsapp|sms|inapp }, task?: { title, taskType, ownerId? }',
  validate(raw) {
    const scheduleInput = raw && raw.schedule;
    if (!scheduleInput || typeof scheduleInput !== 'object' || Array.isArray(scheduleInput)) return null;
    const freq = lower(scheduleInput.freq);
    if (freq !== 'daily' && freq !== 'weekly') return null;
    const hour = Number(scheduleInput.hour);
    const minute = scheduleInput.minute === undefined || scheduleInput.minute === null || scheduleInput.minute === ''
      ? 0
      : Number(scheduleInput.minute);
    if (!Number.isInteger(hour) || hour < 0 || hour > 23) return null;
    if (!Number.isInteger(minute) || minute < 0 || minute > 59) return null;
    const schedule = { freq, hour, minute, timezone: clean(scheduleInput.timezone) || 'Asia/Kolkata' };
    if (freq === 'weekly') {
      const dayOfWeek = Number(scheduleInput.dayOfWeek);
      if (!Number.isInteger(dayOfWeek) || dayOfWeek < 0 || dayOfWeek > 6) return null;
      schedule.dayOfWeek = dayOfWeek;
    }

    const cohortInput = raw && raw.cohort;
    if (!cohortInput || typeof cohortInput !== 'object' || Array.isArray(cohortInput)) return null;
    const readRefs = (input, resourceType) => {
      if (input !== undefined && input !== null && !Array.isArray(input)) return null;
      const refs = [];
      for (const value of (Array.isArray(input) ? input : [])) {
        const reference = clean(typeof value === 'string' ? value : value && value.reference);
        const id = reference.replace(new RegExp(`^${resourceType}/`), '');
        if (!UUID_RE.test(id)) return null;
        const full = `${resourceType}/${id}`;
        if (!refs.includes(full)) refs.push(full);
      }
      return refs;
    };
    const patients = readRefs(cohortInput.patients, 'Patient');
    const staff = readRefs(cohortInput.staff, 'Practitioner');
    if (patients === null || staff === null) return null;
    const query = clean(cohortInput.query);
    if (!patients.length && !staff.length && !query) return null;
    const cohort = {
      ...(patients.length ? { patients } : {}),
      ...(staff.length ? { staff } : {}),
      ...(query ? { query } : {}),
    };

    const action = lower(raw && raw.action) || 'send-message';
    if (!JOB_ACTIONS.includes(action)) return null;
    if (action === 'create-task') {
      const task = raw && raw.task;
      if (!task || typeof task !== 'object' || Array.isArray(task)) return null;
      const title = clean(task.title);
      const taskType = lower(task.taskType || task.type);
      const ownerId = clean(task.ownerId);
      if (!title || PLACEHOLDER_TITLE_RE.test(title) || !TASK_TYPES.includes(taskType)) return null;
      if (ownerId && !UUID_RE.test(ownerId)) return null;
      return { action, schedule, cohort, task: { title, taskType, ...(ownerId ? { ownerId } : {}) } };
    }

    const message = raw && raw.message;
    if (!message || typeof message !== 'object' || Array.isArray(message)) return null;
    const text = clean(message.text);
    const channel = lower(message.channel);
    if (!text || !CHANNELS.includes(channel)) return null;
    return { action, schedule, cohort, message: { text, channel } };
  },
};

const CHANNEL_LABEL = { whatsapp: 'WhatsApp', sms: 'SMS', inapp: 'In-app' };

/**
 * Human confirmation copy for an automation proposal. Shared by every host so
 * the native and web confirmation boundaries describe the exact same write.
 */
export function automationProposalLabel(action) {
  const value = action || {};
  const when = scheduleSummary(value.schedule);
  const who = cohortSummary(value.cohort);
  if ((value.action || 'send-message') === 'create-task') {
    return `New automation: ${when}, create ${taskSummary(value.task)} for ${who}`;
  }
  const channel = CHANNEL_LABEL[value.message && value.message.channel] || '';
  const text = clean(value.message && value.message.text);
  const quoted = text.length > 90 ? `“${text.slice(0, 88).trimEnd()}…”` : `“${text}”`;
  return `New automation: ${when}, send ${channel || 'a message'} to ${who} — ${quoted}`;
}

/**
 * The executable surface-action spec for the universal command agent. The model
 * still only PROPOSES through createAutomationProposalSpec; execute() is called
 * by the host exclusively after explicit confirmation. createJobViaMedplum uses
 * the registered logged-in client, so RLS remains the only authorization lock.
 */
export const createAutomationActionSpec = {
  ...createAutomationProposalSpec,
  surfaces: ['automations'],
  selfSufficient: true,
  nativeLabel: 'Automations',
  iconName: 'CalendarDays',
  async execute(action, ctx = {}) {
    const { job } = await createJobViaMedplum({
      action: action.action || 'send-message',
      schedule: action.schedule,
      cohort: action.cohort,
      message: action.message,
      task: action.task,
    });
    const onChanged = ctx.actionContext && ctx.actionContext.onChanged;
    if (typeof onChanged === 'function') onChanged();
    return {
      ok: true,
      id: job && job.id,
      note: `Automation created — ${scheduleSummary(action.schedule)}, ${cohortSummary(action.cohort)}.`,
    };
  },
  label: automationProposalLabel,
};

const reference = (value) => (typeof value === 'string' ? value : value && value.reference) || null;

export function proposalToForm(proposal) {
  const value = proposal && typeof proposal === 'object' ? proposal : {};
  const missing = [];
  const action = value.action === 'create-task' ? 'create-task' : 'send-message';
  const schedule = value.schedule && typeof value.schedule === 'object' ? value.schedule : null;
  const freq = schedule && (schedule.freq === 'daily' || schedule.freq === 'weekly') ? schedule.freq : null;
  const time = schedule ? toTimeValue(schedule.hour, schedule.minute) : '';
  if (!freq || !time) missing.push('when it runs');

  const cohort = value.cohort && typeof value.cohort === 'object' ? value.cohort : {};
  const patients = (Array.isArray(cohort.patients) ? cohort.patients : []).map(reference).filter(Boolean);
  const staff = (Array.isArray(cohort.staff) ? cohort.staff : []).map(reference).filter(Boolean);
  const query = typeof cohort.query === 'string' ? cohort.query.trim() : '';
  if (!patients.length && !staff.length && !query) missing.push('who it is for');

  const form = {
    action,
    freq: freq || 'weekly',
    dayOfWeek: schedule && Number.isFinite(Number(schedule.dayOfWeek)) ? String(Number(schedule.dayOfWeek)) : null,
    time,
    patients,
    staff,
    query,
    message: null,
    task: null,
  };
  if (action === 'create-task') {
    const task = value.task && typeof value.task === 'object' ? value.task : {};
    const title = clean(task.title);
    if (!title) missing.push('the task title');
    form.task = { title, taskType: task.taskType || null, ownerId: task.ownerId || '' };
  } else {
    const message = value.message && typeof value.message === 'object' ? value.message : {};
    const text = clean(message.text);
    if (!text) missing.push('the message');
    form.message = { text, channel: message.channel || null };
  }
  return { form, missing };
}

export async function askForAutomation({ medplum, user, text, messages = [] }) {
  if (!medplum) throw new Error('askForAutomation: a logged-in MedplumClient is required');
  const utterance = clean(text);
  if (!utterance) throw new Error('Say or type what the automation should do.');
  const result = await askGlobalAgent({
    medplum,
    user,
    pageContext: { surface: 'automations', path: '/automations' },
    messages: [...messages, { role: 'user', content: utterance }],
    surfaceActions: [createAutomationProposalSpec],
    surfaceContext: 'The user is filling in the New automation form. Propose exactly one create_automation action only when who, when, and exact wording are known. It will pre-fill a form for review and will not run. '
      + 'If you are not calling create_automation this turn, your reply text is a plain QUESTION asking for the one missing detail — it must never claim you already proposed, created, or scheduled anything, because you did not: the form is still empty.',
  });
  const proposal = (Array.isArray(result.proposedActions) ? result.proposedActions : [])
    .find((action) => action && action.type === 'create_automation') || null;
  return { proposal, reply: result.reply || '', proposedActions: result.proposedActions || [] };
}
